import { useDisclosure } from "@mantine/hooks";
import { DataTableSortStatus } from "mantine-datatable";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { useInvoices } from "../../../api/hooks/invoice/useInvoices.ts";
import { useUpdateInvoice } from "../../../api/hooks/invoice/useUpdateInvoice.ts";
import { useCollectors } from "../../../api/hooks/user/useCollectors.ts";
import {
  DateRangeFilterInput,
  InvoiceFilter,
  InvoiceOrderBy,
  OrderBy,
  UpdateInvoiceInput,
} from "../../../graphql/generated";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { Invoice } from "../../../types/invoice/invoice.ts";
import { getPagesCount } from "../../../utils/pagination";
import Table from "../../Table/Table.tsx";
import { SortDirection, TableRow } from "../../Table/types.ts";
import { useInvoicesTableData } from "./hooks/useInvoicesTableData.tsx";
import { UpdateInvoiceModal } from "./UpdateInvoiceModal.tsx";
import { getInvoiceApiSort } from "./utils";

export interface InvoicesTableRef {
  refresh: () => void;
}

interface InvoicesTableProps {
  filter: InvoiceFilter;
  canEditCollectorAssigned: boolean;
  canEditInvoice?: boolean;
  lastPaymentDateFilter?: DateRangeFilterInput | undefined;
  onClickRow?: (invoice: Invoice) => void;
}

export const InvoicesTable = forwardRef(
  (
    {
      filter,
      canEditCollectorAssigned,
      canEditInvoice = false,
      lastPaymentDateFilter,
      onClickRow,
    }: InvoicesTableProps,
    ref
  ) => {
    const [modalOpened, { open: openModal, close: closeModal }] =
      useDisclosure(false);

    const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();

    const [orderBy, setOrderBy] = useState<InvoiceOrderBy>({
      invoiceDueDate: OrderBy.Desc,
    });

    const [page, setPage] = useState<number>(1);

    const [{ data: invoicesData, fetching }, refreshInvoices] = useInvoices({
      filter,
      orderBy,
      page,
      lastPaymentDate: lastPaymentDateFilter,
    });

    const invoices = invoicesData?.invoices?.data;

    const pageCount = getPagesCount(invoicesData?.invoices?.total);

    const [{ data: collectorsData }] = useCollectors();

    const [, updateInvoice] = useMutationNotificationWrapper(
      useUpdateInvoice(),
      {
        success: { message: "Invoice has been updated." },
      }
    );

    const handleOpenModal = useCallback(
      (invoiceId: number) => {
        if (!canEditInvoice) {
          return;
        }
        const invoice = invoices?.find(
          (item: Invoice) => item.id === invoiceId
        );
        setSelectedInvoice(invoice);
        openModal();
      },
      [canEditInvoice, invoices, openModal]
    );

    const handleCloseModal = useCallback(() => {
      closeModal();
      setSelectedInvoice(undefined);
    }, [closeModal]);

    const handleUpdateInvoice = useCallback(
      (id: number | undefined, input: UpdateInvoiceInput) => {
        if (!id) {
          return;
        }
        updateInvoice({ id, input }).then((data) => {
          if (!data.error) {
            handleCloseModal();
            refreshInvoices();
          }
        });
      },
      [handleCloseModal, refreshInvoices, updateInvoice]
    );

    const [columns, rows] = useInvoicesTableData({
      data: invoices,
      collectors: collectorsData?.users.data,
      canEditCollectorAssigned,
      canEditInvoice,
      onInlineEdit: handleUpdateInvoice,
      onEdit: handleOpenModal,
    });

    const handleOnSortChange = useCallback(
      (sort: DataTableSortStatus) => {
        setOrderBy(getInvoiceApiSort(sort));
      },
      [setOrderBy]
    );

    const handlePageChange = useCallback(
      (page: number) => {
        setPage(page);
      },
      [setPage]
    );

    const handleInvoiceClick = (record: TableRow) => {
      if (!invoices) {
        return;
      }
      const invoice = invoices.find((item: Invoice) => item.id === record.id);
      if (!invoice) {
        return;
      }
      onClickRow && onClickRow(invoice);
    };

    useEffect(() => {
      setPage(1);
    }, [filter]);

    useImperativeHandle(
      ref,
      (): InvoicesTableRef => ({
        refresh: refreshInvoices,
      })
    );

    return (
      <>
        <Table
          columns={columns}
          rows={rows}
          defaultSort={{
            columnAccessor: "invoiceNumber",
            direction: SortDirection.asc,
          }}
          pagination={{
            pageCount: pageCount,
            page: page,
            onPageChange: handlePageChange,
          }}
          loading={fetching}
          onSortChange={handleOnSortChange}
          onRowClick={handleInvoiceClick}
        />
        {modalOpened && selectedInvoice && (
          <UpdateInvoiceModal
            data={selectedInvoice}
            opened={true}
            onClose={handleCloseModal}
            onSubmit={(value) =>
              handleUpdateInvoice(selectedInvoice?.id, value)
            }
          />
        )}
      </>
    );
  }
);

InvoicesTable.displayName = "InvoicesTable";
